import { useEffect, useRef, type JSX } from 'react';

type Props = {
  onClickOutside: () => void;
  children: React.ReactNode;
};

export default function OutsideClickHandler({
  onClickOutside,
  children,
}: Props): JSX.Element {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent | TouchEvent) {
      const target = event.target as Node;
      // A surface this component owns may be PORTALED elsewhere in the DOM — a
      // date picker's calendar, a select's menu — and is then not inside `ref`
      // even though the user is plainly still interacting with the thing this
      // handler is wrapping. Closing on it is why picking a month inside a modal
      // dismissed the modal. Such a surface marks itself and is skipped here.
      if (target instanceof Element && target.closest('[data-outside-click-ignore]')) {
        return;
      }
      if (ref.current && !ref.current.contains(target)) {
        onClickOutside();
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [onClickOutside]);

  return <div ref={ref}>{children}</div>;
}
